import { useState } from "react";
import { ChevronDown, ChevronRight, Heart, MessageCircle, Play, X } from "lucide-react";
import { AsOfMarker, Button, EmptyState, FormDialog, Icon, Page, Skeleton, StatBlock, DarkHero, PlatformIcon, Spinner, compactNumber } from "@cntrl/ui";
import { useCapability } from "../../app/bandContext.js";
import { useMediaObjectUrl } from "../../app/hooks/useMedia.js";
import { ApiError, errorMessage } from "../../app/hooks/apiError.js";
import { pushToast } from "../../app/toasts.js";
import {
  useContentStats,
  useSavedIdeas,
  useGenerateHashtags,
  useGenerateIdeas,
  useSaveIdea,
  useUpdateIdeaStatus,
  useDeleteIdea,
  useClearIdeas,
  type ContentView,
  type ContentPlatform,
  type ContentIdea,
  type SavedIdeaView,
} from "../../app/hooks/useContentStats.js";
import styles from "./ContentScreen.module.scss";
import { GetTheData } from "./GetTheData.js";
import { COOKING_LINES, pickCookingLine } from "./cookingLines.js";
import { confidenceTier, localizeBestTime, TIER_WORD } from "./bestTimeFormat.js";
import { usePhoneLayout } from "../../app/phoneLayout.js";

type ContentQuery = ReturnType<typeof useContentStats>;

// Exact designed copy (acceptance) — the empty states are asserted verbatim by the feature test.
const REACH_EMPTY = "Connect a platform to see analytics";
// The REACH HERO's own empty copy, verbatim from the spec and matching legacy (Content.tsx:84/95). Kept
// separate from REACH_EMPTY on purpose: that constant is also the PER-PLATFORM panel's empty title, where
// "link a platform to see your total reach" would be wrong — the panel is about one platform, not the total.
const REACH_HERO_EMPTY_TITLE = "Connect a platform to begin";
const REACH_HERO_EMPTY_DESC = "Connecting… link a platform to see your total reach.";
// Best times has TWO absent states and they point at different problems, so they must not share a string:
// nothing connected is theirs to fix (link an account), too few posts is just time. Collapsing them is the
// same "connected-with-no-figure" mistake the socials tiles carried — it sends a band to reconnect something
// that is already connected. Legacy's UAT prescribes both (D-CONT-06.3).
const BEST_TIMES_EMPTY = "Not enough data yet — keep posting and we'll learn your audience's rhythm.";
const BEST_TIMES_DISCONNECTED = "Connect a platform to see your best posting times.";
const TOP_CREATORS_EMPTY = "No top followers surfaced yet.";
const TOP_POSTS_EMPTY = "No posts yet for this platform.";

/** The best-time confidence dot. Green/amber/grey by rank, but NEVER colour alone (WCAG 1.4.1) — the tier is
 *  surfaced as the dot's accessible label. */
function ConfidenceDot({ rank }: { rank: number }) {
  const tier = confidenceTier(rank);
  const alt = `Confidence: ${TIER_WORD[tier]}`;
  return <span className={styles.confDot} data-tier={tier} role="img" aria-label={alt} title={alt} />;
}

const PLATFORM_LABELS: Record<string, string> = {
  instagram: "Instagram",
  facebook: "Facebook",
  tiktok: "TikTok",
  youtube: "YouTube",
  threads: "Threads",
};
const label = (p: string | null | undefined) => (p ? (PLATFORM_LABELS[p] ?? p) : "—");

/** The hero chips are two-letter codes, not full names — four full labels do not fit one row at hero scale. */
const PLATFORM_CODES: Record<string, string> = {
  instagram: "IG", facebook: "FB", tiktok: "TT", youtube: "YT", threads: "TH",
};
const code = (p: string) => PLATFORM_CODES[p] ?? p.slice(0, 2).toUpperCase();
const num = (n: number | null | undefined) => (n != null ? compactNumber(n) : "—");

/** One module shell — the box + the four-state contract (skeleton / error+retry / empty / content). */
// ---- reach hero -----------------------------------------------------------------------------------------

function ReachHeroModule({ q }: { q: ContentQuery }) {
  const reach = q.data?.reach;
  const chips = reach?.platforms ?? [];
  const empty = !reach || reach.totalFollowers == null;
  // Mobile names the number with a "Total followers" EYEBROW above it and drops the sub-line (Ryan, ART-184
  // round two: "make total followers the eyebrow") — the phone hero is the big figure with one label over it,
  // nothing under it. Desktop keeps the "Reach · total followers" eyebrow + the across-platforms sub-line.
  const isMobile = usePhoneLayout();
  // The press-photo backdrop. `DarkHero` has carried a `photoUrl` slot since the Spotlight extraction; what
  // was missing was the resolver, and `useMediaObjectUrl` is it (the same hook the tour row and the band
  // switcher use). Null resolves to DarkHero's own gradient, so a band with no photo is unaffected.
  const photo = useMediaObjectUrl(reach?.photoMediaId ?? null, null);

  // NOT wrapped in a ContentBox. The hero IS the card here — a white panel titled "Reach" around a dark
  // hero that already says "REACH · TOTAL FOLLOWERS" is the same heading twice, and it caps the hero's
  // height so it cannot sit level with the AI rail beside it. The empty state lives INSIDE the hero for the
  // same reason: the spec's first-run specimen shows the connect copy on the dark surface, not on a white
  // card that has replaced it.
  return (
    <DarkHero
      eyebrow={
        // Desktop drops the "Reach · total followers" eyebrow and its star (Ryan, ART-204). Mobile keeps the
        // "Total followers" label dropped onto the number (`.reachEyebrowLower`, ART-184).
        isMobile && !empty ? <span className={styles.reachEyebrowLower}>Total followers</span> : null
      }
      photoUrl={photo.url ?? null}
      headGap={isMobile ? "snug" : "default"}
      className={styles.reachHero}
    >
      {q.isPending ? (
        <Skeleton height={64} />
      ) : q.isError ? (
        // Kept from the ContentBox shell this module no longer uses: a hero that renders "—" on a FAILED
        // load is indistinguishable from a band with no followers, which is the worse of the two lies.
        <EmptyState
          title="Couldn't load"
          description={q.error instanceof ApiError ? q.error.message : "Please try again."}
          action={<Button onClick={() => void q.refetch()}>Retry</Button>}
        />
      ) : (
        <>
          <div className={styles.reachLead}>
            {/* EXACT, not compact: this is the headline figure of the page and "74,021" is the number the
                band recognises — 74.0k rounds away the thing they are watching move. The per-platform chips
                below stay compact, because four of them share one row. */}
            <span className={styles.reachValue}>
              {reach?.totalFollowers != null ? reach.totalFollowers.toLocaleString() : "—"}
            </span>
            {/* The "Across Instagram, Facebook, TikTok & YouTube" sub-line is dropped (Ryan, ART-204); only
                the empty state keeps its connect copy. */}
            {empty && <span className={styles.reachSub}>{REACH_HERO_EMPTY_DESC}</span>}
          </div>

          {empty ? (
            <a className={styles.reachCta} href="/settings/connections">
              {REACH_HERO_EMPTY_TITLE}
            </a>
          ) : (
            // Mobile drops the per-platform follower chips (Ryan, ART-184): the phone hero is just the big
            // number. Desktop keeps the breakdown — a platform LOGO + its count (Ryan, ART-204: the colour
            // dot + abbreviation are replaced by the real mark).
            !isMobile && chips.length > 0 && (
              <div className={styles.reachChips}>
                {chips.map((c) => (
                  <span key={c.platform} className={styles.reachChip} data-platform={c.platform}>
                    <PlatformIcon platform={c.platform ?? ""} size={16} />
                    <span className={styles.reachChipValue}>{num(c.followers)}</span>
                    <span className={styles.srOnly}>{label(c.platform)}</span>
                  </span>
                ))}
              </div>
            )
          )}
        </>
      )}
    </DarkHero>
  );
}

// ---- per-platform breakdown (pills + the active platform's panel) ---------------------------------------

/** The KPIs / Best Times / Top Creators / Top 5 / UGC panel for one platform — shared by the tab + the drill-down. */
export function PlatformPanel({ platform }: { platform: ContentPlatform }) {
  // Mobile drops Top Creators and the UGC tracker (Ryan, ART-184): the phone shows the KPIs, best times and
  // the top posts, and nothing else per platform.
  const isMobile = usePhoneLayout();
  // Both halves are optional on the wire (the generated schema marks value types optional), and a row missing
  // either one cannot be rendered or ranked. Filter BEFORE the map so the surviving rows' indices — which ARE
  // the confidence tiers — stay contiguous: dropping inside the map would leave a gap in the ranking.
  const bestTimes = (platform.bestTimes ?? []).flatMap((t) =>
    t.dayOfWeek != null && t.utcHour != null ? [{ dayOfWeek: t.dayOfWeek, utcHour: t.utcHour }] : [],
  );
  const topCreators = platform.topCreators ?? [];
  const topPosts = platform.topPosts ?? [];
  return (
    <>
      {/* The account strip ("handle · connected · Switch account") is dropped (Ryan, ART-184 — "hide it on
          desktop too"): the platform is already named by the selector tab, and connecting/switching lives in
          Settings › Connections. */}
      <div className={styles.box}>
        {/* KPI band: the platform's mark hard left, the figures pushed right (Ryan, ART-184 — the account
            strip that used to name the platform is gone, so the mark identifies whose numbers these are). */}
        <div className={styles.kpiBand}>
          <span className={styles.kpiMark} aria-hidden="true">
            <PlatformIcon platform={platform.platform} size={24} />
          </span>
          <div className={styles.kpis}>
            <StatBlock label="Followers" value={platform.followers} />
            {/* Label + value both come from the payload, together. TikTok stamps "Likes" (account-level),
                because its lifetimeViews is a sum over ~60 fetched videos; YouTube stamps "Lifetime views". */}
            <StatBlock label={platform.viewsLabel ?? "Views"} value={platform.views} />
            {/* `exact` so 3.2 stays 3.2 rather than going through the compact-number formatter, and the
                null → '—' contract stays where StatBlock owns it. Server sends null, never a 0%.
                HIDDEN entirely for a platform that cannot report engagement (Facebook: its /published_posts
                posts carry no likes/comments — the #10 limitation), where a permanent "—" would read as
                "not loaded yet" rather than "not applicable". `engagementSupported` is the server's verdict. */}
            {platform.engagementSupported && (
              <StatBlock label="Eng. rate" value={platform.engagement} suffix="%" exact />
            )}
          </div>
          {/* The "Live · Meta API" badge is dropped (Ryan, ART-184) — it is the platform's own attribution
              chip and the numbers do not need it; the account card already says the account is connected. */}
        </div>

        <div className={styles.cols}>
          <div className={styles.col}>
            <h3 className={styles.colHeading}>Best days &amp; times to post</h3>
            {bestTimes.length === 0 ? (
              <p className={styles.seam}>
                {platform.connected ? BEST_TIMES_EMPTY : BEST_TIMES_DISCONNECTED}
              </p>
            ) : (
              <ol className={styles.bestTimes}>
                {bestTimes.map((t, i) => {
                  const { day, time } = localizeBestTime(t.dayOfWeek, t.utcHour);
                  return (
                    <li key={`${t.dayOfWeek}-${t.utcHour}`} className={styles.bestTimeRow}>
                      <span className={styles.rank}>{i + 1}</span>
                      <span className={styles.bestTimeText}>
                        <strong>{day}</strong> at {time}
                      </span>
                      <ConfidenceDot rank={i} />
                    </li>
                  );
                })}
              </ol>
            )}
          </div>

          {/* A category the platform cannot fill is HIDDEN, never drawn empty (PO, 2026-10-03, ART-204): not every
              platform has every category, and an "on the way" seam over a column no vendor serves (Chartmetric
              has no Facebook notable-followers lane) read as broken. `topCreatorsSupported` is the server's
              verdict — the same shape as `engagementSupported` on the KPI band and `ugc.hidden` below. */}
          {!isMobile && platform.topCreatorsSupported && (
          <div className={styles.col}>
            <div className={styles.colHead}>
              <h3 className={styles.colHeading}>Top followers</h3>
              {/* Freshness ONLY — no provenance label. It used to read "Songstats", which this column has
                  never come from: the backend sources Top Creators from Chartmetric notable-followers, and
                  the mismatch was carried as a comment awaiting a ruling. Ryan gave it (ART-104): drop the
                  word rather than swap it, since the column already sits under a platform heading.
                  The marker reads ONLY when creators exist: `platform.asOf` is the platform-stats projection
                  freshness, NOT the creator refresh, so over an empty list it would claim a fresh update to
                  data that isn't there. */}
              {topCreators.length > 0 && <AsOfMarker asOf={platform.asOf} />}
            </div>
            {topCreators.length === 0 ? (
              <p className={styles.seam}>{TOP_CREATORS_EMPTY}</p>
            ) : (
              <ol className={styles.creators}>
                {topCreators.map((c, i) => (
                  <li key={i} className={styles.creator}>
                    <span className={styles.rank}>{i + 1}</span>
                    {/* Same fail-soft contract as the post thumbs: the avatar hides itself on error so a
                        dead vendor URL leaves the neutral disc, not a broken-image box. */}
                    <span className={styles.avatar} aria-hidden="true">
                      {c.avatarUrl && (
                        <img src={c.avatarUrl} alt="" loading="lazy"
                             onError={(e) => { e.currentTarget.style.display = "none"; }} />
                      )}
                    </span>
                    {/* The @-form ONLY. It is one field from the vendor (legacy maps `username || fullname`
                        into a single string), so rendering it plainly and again with an @ would be the same
                        datum twice dressed as two. */}
                    <span className={styles.creatorName}>{c.handle ? `@${c.handle}` : "—"}</span>
                    <span className={styles.creatorFollowers}>{num(c.followers)} followers</span>
                  </li>
                ))}
              </ol>
            )}
          </div>
          )}

          <div className={styles.col}>
            {isMobile ? (
              <div className={styles.topPostsHead}>
                <p className={styles.eyebrow}>Last 30 days</p>
                <h3 className={styles.colHeading}>Top 5 posts</h3>
                {topPosts.length > 1 && <span className={styles.swipeHint}>Swipe →</span>}
              </div>
            ) : (
              <h3 className={styles.colHeading}>Top 5 · best performing</h3>
            )}
            {topPosts.length === 0 ? (
              <p className={styles.seam}>{TOP_POSTS_EMPTY}</p>
            ) : isMobile ? (
              // Mobile (ART-184): a horizontal swipe of post CARDS — big artwork, caption, metrics — each
              // one a link that opens the post in the platform app. No save-heart (Ryan); the heart in the
              // metrics row is the like COUNT.
              <ol className={styles.postCards}>
                {topPosts.map((p, i) => {
                  const body = (
                    <>
                      <span className={styles.postCardHead}>
                        <span className={styles.postRank}>#{i + 1}</span>
                        <PlatformIcon platform={platform.platform} size={20} />
                      </span>
                      <span className={styles.postCardThumb} aria-hidden="true">
                        {p.thumbnailUrl && (
                          <img src={p.thumbnailUrl} alt="" loading="lazy"
                               onError={(e) => { e.currentTarget.style.display = "none"; }} />
                        )}
                      </span>
                      <span className={styles.postCardCaption}>{p.caption ?? "Untitled"}</span>
                      <span className={styles.postMetrics}>
                        {p.plays != null && <span><Icon icon={Play} size={16} /> {num(p.plays)}</span>}
                        {p.likes != null && <span><Icon icon={Heart} size={16} /> {num(p.likes)}</span>}
                        {p.comments != null && <span><Icon icon={MessageCircle} size={16} /> {num(p.comments)}</span>}
                      </span>
                    </>
                  );
                  return (
                    <li key={i} className={styles.postCard}>
                      {p.url ? (
                        <a className={styles.postCardLink} href={p.url} target="_blank" rel="noreferrer">{body}</a>
                      ) : (
                        <div className={styles.postCardLink}>{body}</div>
                      )}
                    </li>
                  );
                })}
              </ol>
            ) : (
              <ol className={styles.posts}>
                {topPosts.map((p, i) => (
                  <li key={i} className={styles.post}>
                    <span className={styles.rank}>{i + 1}</span>
                    {/* Fail-soft artwork: the thumb sits over a neutral base and REMOVES ITSELF on error, so
                        a dead vendor thumbnail proxy shows the placeholder, never a broken-image box. */}
                    <span className={styles.thumb} aria-hidden="true">
                      {p.thumbnailUrl && (
                        <img src={p.thumbnailUrl} alt="" loading="lazy"
                             onError={(e) => { e.currentTarget.style.display = "none"; }} />
                      )}
                    </span>
                    <span className={styles.postMain}>
                      <span className={styles.postCaption}>
                        {p.url ? (
                          <a href={p.url} target="_blank" rel="noreferrer">{p.caption ?? "Untitled"}</a>
                        ) : (
                          (p.caption ?? "Untitled")
                        )}
                      </span>
                      <span className={styles.postMetrics}>
                        {p.plays != null && <span><Icon icon={Play} size={16} /> {num(p.plays)}</span>}
                        {p.likes != null && <span><Icon icon={Heart} size={16} /> {num(p.likes)}</span>}
                        {p.comments != null && <span><Icon icon={MessageCircle} size={16} /> {num(p.comments)}</span>}
                      </span>
                    </span>
                  </li>
                ))}
              </ol>
            )}
          </div>
        </div>
      </div>

      {/* UGC ("Your songs on …") shows only for TikTok (Ryan, ART-204) — it is the one platform whose
          creator/sound tracking is worth a box; the others hid it behind an empty state. */}
      {!isMobile && platform.platform === "tiktok" && <UgcTracker platform={platform} />}
    </>
  );
}

function UgcTracker({ platform }: { platform: ContentPlatform }) {
  const ugc = platform.ugc ?? { supported: false, tracks: [], asOf: null };
  const tracks = ugc.tracks ?? [];
  // HIDDEN is not UNSUPPORTED. Facebook has no creator lane anyone intends to wire, so the card is dropped
  // rather than promising a "coming soon" that is not coming. Unsupported = later; hidden = never.
  if (ugc.hidden) return null;
  return (
    <div className={styles.box}>
      <div>
        <p className={styles.eyebrow}>Your songs on {label(platform.platform)}</p>
        {/* The same ruling the Top creators column got (ART-104): freshness, never provenance. "Powered by
            Songstats" was wrong on the facts as well as unwanted — `BuildUgc` filters to
            `source='chartmetric'`, so this card has never carried a Songstats row. The marker takes the
            UGC's OWN `asOf` (the max fetch stamp over those posts), not the platform projection's, and
            reads only when there are rows to be fresh ABOUT. */}
        {tracks.length > 0 && <AsOfMarker asOf={ugc.asOf} />}
      </div>
      {!ugc.supported ? (
        <p className={styles.inset}>UGC tracking for {label(platform.platform)} is on the way.</p>
      ) : tracks.length === 0 ? (
        <p className={styles.inset}>No per-release UGC activity yet.</p>
      ) : (
        <ol className={styles.ugc}>
          {tracks.map((t, i) => (
            <li key={i} className={styles.ugcRow}>
              <span className={styles.ugcTitle}>{t.title}</span>
              <span className={styles.ugcCreators}>
                {t.creators} creators{t.reach != null ? ` · ${num(t.reach)} reach` : ""}
              </span>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

function PlatformModule({ q }: { q: ContentQuery }) {
  const platforms = q.data?.platforms ?? [];
  const firstConnected = platforms.find((p) => p.connected)?.platform ?? platforms[0]?.platform ?? "instagram";
  const [active, setActive] = useState<string | null>(null);
  const activeKey = active ?? firstConnected;
  const activePlatform = platforms.find((p) => p.platform === activeKey);
  // Spec §3: "mobile per-platform TILES". The desktop breakdown is a tablist that shows one platform at a
  // time; below the shell swap (`lg`) every platform is stacked as its own tile instead — each PlatformPanel
  // already self-identifies (its account card), so the tablist is dropped rather than duplicated. The data
  // and the Views(30D) relabel are identical either way (ART-153 pass two).
  const isDesktop = !usePhoneLayout();

  const noneEmpty = (
    <div className={styles.box}>
      <EmptyState title={REACH_EMPTY} description="Connect this platform in Settings → Connections." />
    </div>
  );

  return (
    <section className={styles.breakdown} aria-labelledby="content-platforms-heading">
      {/* The section head sits ON THE PAGE, not inside a card: the tabs switch which card is shown below, so
          a header that lived inside the card would be re-rendered by its own control. */}
      <header className={styles.sectionHead} data-mobile={!isDesktop || undefined}>
        {/* The section eyebrow + heading are desktop-only (Ryan, ART-184): on the phone the selector sits
            directly under the hero. The heading stays as the section's accessible name, visually hidden. */}
        {isDesktop ? (
          <div>
            <p className={styles.eyebrow}>Per-platform breakdown</p>
            <h2 id="content-platforms-heading" className={styles.sectionTitle}>How each channel&apos;s performing.</h2>
          </div>
        ) : (
          <h2 id="content-platforms-heading" className={styles.srOnly}>How each channel&apos;s performing.</h2>
        )}
        {/* The 4-way switcher, on BOTH shells now (it used to be desktop-only, with mobile stacking tiles). */}
        <div role="tablist" aria-label="Platform" className={styles.platformTabs}>
          {platforms.map((p) => (
            <button
              key={p.platform}
              role="tab"
              aria-selected={p.platform === activeKey}
              className={p.platform === activeKey ? `${styles.platformTab} ${styles.platformTabActive}` : styles.platformTab}
              onClick={() => setActive(p.platform ?? null)}
            >
              {/* Desktop tabs carry the brand mark; the mobile segmented control is text-only (Ryan's mock). */}
              {isDesktop && <PlatformIcon platform={p.platform} size={20} />}
              <span>{label(p.platform)}</span>
            </button>
          ))}
        </div>
      </header>

      {q.isPending ? (
        <div className={styles.box}><Skeleton height={220} /></div>
      ) : q.isError ? (
        <div className={styles.box}>
          <EmptyState
            title="Couldn't load"
            description={q.error instanceof ApiError ? q.error.message : "Please try again."}
            action={<Button onClick={() => void q.refetch()}>Retry</Button>}
          />
        </div>
      ) : activePlatform ? (
        // ART-184: the phone now uses the SAME one-at-a-time selector as desktop (Ryan wants the 4-way switcher
        // under the hero), rather than stacking every platform as its own tile.
        <PlatformPanel platform={activePlatform} />
      ) : (
        noneEmpty
      )}
    </section>
  );
}

// ---- hashtag generator ----------------------------------------------------------------------------------

/** Hidden (ART-260): the tags are Gemini-only with no real post counts, and the PO's ask is tags in a
 *  verified 100K–1M range. No official Instagram/TikTok API exposes a hashtag's count, and the third-party
 *  APIs that do are scraped against both platforms' terms. ART-277 researches a source; the card, its hook
 *  and the backend route stay intact so re-enabling is this one flag. */
const SHOW_HASHTAG_GENERATOR = false;

function HashtagModule() {
  const [platform, setPlatform] = useState("instagram");
  const [desc, setDesc] = useState("");
  // Every generated tag starts SELECTED (spec): the common case is "give me tags for this post", so making
  // the band tick sixteen boxes before Copy-all does anything would be work for its own sake. Tapping
  // deselects. Keyed by tag text, which is what Copy-all joins.
  const [dropped, setDropped] = useState<Set<string>>(new Set());
  const gen = useGenerateHashtags();
  const buckets = gen.data;

  const submit = () => {
    if (!desc.trim()) return;
    setDropped(new Set());   // a fresh generation starts fully selected again
    gen.mutate(
      { platform, description: desc.trim() },
      { onError: (e) => pushToast(errorMessage(e, "Couldn't generate hashtags — try again."), "error") },
    );
  };

  const groups: { label: string; tags: string[] }[] = [
    { label: "High reach", tags: buckets?.high ?? [] },
    { label: "Mid reach", tags: buckets?.mid ?? [] },
    { label: "Niche", tags: buckets?.niche ?? [] },
  ].filter((g) => g.tags.length > 0);
  const all = groups.flatMap((g) => g.tags);
  const selected = all.filter((t) => !dropped.has(t));

  const toggle = (t: string) =>
    setDropped((prev) => {
      const next = new Set(prev);
      if (!next.delete(t)) next.add(t);
      return next;
    });

  return (
    <section className={styles.box} aria-labelledby="content-hashtags-heading">
      <header className={styles.head}>
        <h3 id="content-hashtags-heading" className={styles.title}>Hashtag generator</h3>
      </header>

      {/* Short CODES here, unlike the per-platform tabs' full names: three of them sit above an input on a
          half-width card, where "Instagram / TikTok / YouTube" wraps. */}
      <div className={styles.miniPills}>
        {["instagram", "tiktok", "youtube"].map((p) => (
          <button
            key={p}
            /* Names the JOB, not just the platform. The pill reads "IG", so it needs a label — but a bare
               "Instagram" collides with the Need-ideas tile of the same name elsewhere on this page, leaving
               a screen-reader user two identical buttons that do different things. */
            aria-label={`Hashtags for ${label(p)}`}
            className={p === platform ? `${styles.miniPill} ${styles.pillActive}` : styles.miniPill}
            onClick={() => setPlatform(p)}
          >
            {code(p)}
          </button>
        ))}
      </div>

      <div className={styles.aiControls}>
        <input
          className={styles.aiInput}
          placeholder="Describe the post…"
          value={desc}
          onChange={(e) => setDesc(e.target.value)}
          aria-label="Describe the post"
        />
        <Button variant="accent" size="field" onClick={submit} disabled={gen.isPending || !desc.trim()}>
          {gen.isPending ? "Generating…" : "Generate"}
        </Button>
      </div>

      {gen.isError ? (
        <p className={styles.inset}>Couldn&apos;t generate hashtags — try again.</p>
      ) : all.length === 0 ? null : (
        <div className={styles.hashtags}>
          {groups.map((g) => (
            <div key={g.label} className={styles.tagBucket}>
              <p className={styles.eyebrow}>{g.label}</p>
              <div className={styles.tagChips}>
                {g.tags.map((t) => (
                  <button
                    key={t}
                    aria-pressed={!dropped.has(t)}
                    className={dropped.has(t) ? styles.tagChip : `${styles.tagChip} ${styles.tagChipOn}`}
                    onClick={() => toggle(t)}
                  >
                    {t}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {all.length > 0 && (
        <div className={styles.tagFooter}>
          <span className={styles.tagCount}>{selected.length} selected</span>
          <Button
            size="sm"
            disabled={selected.length === 0}
            onClick={() => {
              void navigator.clipboard?.writeText(selected.join(" "));
              pushToast("Copied all hashtags", "success");
            }}
          >
            Copy all
          </Button>
        </div>
      )}
    </section>
  );
}

// ---- "Need ideas" + save ---------------------------------------------------------------------------------


function IdeasModule() {
  // xs is desktop-only (under the 44pt touch minimum); the dialog is a bottom sheet on a phone.
  const isDesktop = !usePhoneLayout();
  // FOUR platforms here, unlike the hashtag generator's three: the spec and the legacy app both offer
  // Instagram · Facebook · TikTok · YouTube for ideas, and `IContentAiService` already maps "facebook".
  // The hashtag generator stays IG/TT/YT — that one the spec really does scope to three tabs.
  const [platform, setPlatform] = useState("instagram");
  // The results live in a dialog (ART-276), opened at the tap so the wait has somewhere to show.
  const [open, setOpen] = useState(false);
  const [cookingLine, setCookingLine] = useState(COOKING_LINES[0]!);
  // Which ideas in THIS batch were saved, so the button can say so; cleared on every new batch.
  const [saved, setSaved] = useState<ReadonlySet<number>>(new Set());
  const gen = useGenerateIdeas();
  const save = useSaveIdea();
  const ideas = gen.data?.ideas ?? [];

  // Takes the platform EXPLICITLY. The tiles both select and generate in one click, and `setPlatform` does
  // not apply until the next render — reading it from state here would generate for the PREVIOUS tile.
  const generate = (p: string = platform) => {
    setCookingLine(pickCookingLine());
    setSaved(new Set());
    gen.mutate({ platform: p });
  };

  const close = () => {
    setOpen(false);
    gen.reset();
  };

  const saveIdea = (idea: ContentIdea, i: number) =>
    save.mutate(
      { platform, title: idea.title ?? null, body: idea.description ?? null },
      {
        onSuccess: () => {
          setSaved((s) => new Set(s).add(i));
          pushToast("Saved to Content I'm trying", "success");
        },
        onError: (e) => pushToast(errorMessage(e, "Couldn't save idea."), "error"),
      },
    );

  return (
    <section className={styles.box} aria-labelledby="content-ideas-heading">
      <header className={styles.head}>
        <h3 id="content-ideas-heading" className={styles.title}>
          Need ideas?
        </h3>
      </header>
      {/* No "tap a platform" instruction (Ryan, ART-184) — the brand tiles are self-evidently tappable. */}
      <div className={styles.aiControls}>
        {/* Brand TILES in a 2-up grid, not a pill row: the tile IS the affordance ("tap a platform"), so
            each one generates on click rather than selecting a platform for a separate button. No selected
            state, deliberately — these are four ACTIONS, and highlighting one made the other three look
            unavailable rather than merely unclicked. `platform` is still tracked, but only to title the
            results dialog. */}
        <div className={styles.ideaTiles}>
          {["instagram", "facebook", "tiktok", "youtube"].map((p) => (
            <button
              key={p}
              className={styles.ideaTile}
              onClick={() => { setPlatform(p); setOpen(true); generate(p); }}
              disabled={gen.isPending}
            >
              <PlatformIcon platform={p} size={24} />
              <span>{label(p)}</span>
            </button>
          ))}
        </div>
      </div>
      {open && (
        <FormDialog
          title={`${label(platform)} ideas`}
          onClose={close}
          dirty={false}
          // No header while it cooks: the wait is just the line + spinner. The title still names the dialog.
          hideHeader={gen.isPending}
          footer={
            gen.isPending ? null : (
              <Button variant="ghost" onClick={() => generate()}>
                {gen.isError ? "Try again" : "More ideas"}
              </Button>
            )
          }
        >
          {gen.isPending ? (
            <div className={styles.cooking}>
              <p className={styles.cookingLine}>{cookingLine}</p>
              <Spinner size={24} label={cookingLine} />
            </div>
          ) : gen.isError ? (
            <p className={styles.seam}>{errorMessage(gen.error, "Couldn't get ideas — try again.")}</p>
          ) : ideas.length === 0 ? (
            <p className={styles.seam}>No ideas came back — try again.</p>
          ) : (
            <ol className={styles.ideas}>
              {ideas.map((idea, i) => (
                <li key={i} className={styles.idea}>
                  <span className={styles.ideaTitle}>{idea.title}</span>
                  <span className={styles.ideaBody}>{idea.description}</span>
                  {saved.has(i) ? (
                    <span className={styles.tryThisDone}>Saved</span>
                  ) : (
                    <Button
                      variant="accent"
                      size={isDesktop ? "xs" : "sm"}
                      className={styles.tryThis}
                      onClick={() => saveIdea(idea, i)}
                      disabled={save.isPending}
                    >
                      I'm trying this
                    </Button>
                  )}
                </li>
              ))}
            </ol>
          )}
        </FormDialog>
      )}
    </section>
  );
}

// ---- "Content I'm trying" saved ideas --------------------------------------------------------------------

function SavedIdeasModule() {
  const q = useSavedIdeas();
  const update = useUpdateIdeaStatus();
  const remove = useDeleteIdea();
  const clear = useClearIdeas();
  const items = q.data?.items ?? [];
  // Which row is open. One at a time: these are short rows and keeping several expanded turns the card into
  // a wall with no ranking.
  const [open, setOpen] = useState<string | null>(null);

  const setStatus = (idea: SavedIdeaView, status: "trying" | "done" | "dropped") =>
    update.mutate(
      { ideaId: idea.ideaId ?? "", status: idea.status === status ? "trying" : status },
      { onError: (e) => pushToast(errorMessage(e, "Couldn't update."), "error") },
    );

  return (
    <section className={styles.box} aria-labelledby="content-trying-heading">
      <header className={styles.head}>
        <h3 id="content-trying-heading" className={styles.title}>Content I&apos;m trying</h3>
        {items.length > 0 && (
          <button
            className={styles.clear}
            onClick={() =>
              clear.mutate(undefined, { onError: (e) => pushToast(errorMessage(e, "Couldn't clear."), "error") })
            }
          >
            Clear
          </button>
        )}
      </header>
      {/* Subtext removed (Ryan, ART-204) — the "I'm trying this" affordance elsewhere already says what this is. */}

      {q.isLoading ? (
        <Skeleton variant="line" width="60%" />
      ) : items.length === 0 ? (
        <EmptyState title="Nothing saved yet" />
      ) : (
        <ul className={styles.saved}>
          {items.map((idea) => {
            const id = idea.ideaId ?? "";
            const isOpen = open === id;
            return (
              <li key={id} className={styles.savedRow}>
                <div className={styles.savedHead}>
                  <span className={styles.savedMark} aria-hidden="true">
                    <PlatformIcon platform={idea.platform} size={20} />
                  </span>
                  <span className={styles.savedText}>
                    <span className={styles.savedTitle}>{idea.title ?? "Untitled idea"}</span>
                    <span className={styles.savedDate}>
                      {idea.createdOn ? new Date(idea.createdOn).toLocaleDateString() : ""}
                      {idea.status === "done" && " · ✓ Worked"}
                      {idea.status === "dropped" && " · Didn't work"}
                    </span>
                  </span>
                  {/* The arrow opens the verdict controls. It is only ever rendered because there IS
                      something behind it — the spec's own audit note is that a row with nothing to open
                      still rendered an arrow, and a dead affordance is worse than none. */}
                  <button
                    className={styles.roundBtn}
                    aria-expanded={isOpen}
                    aria-label={isOpen ? "Collapse idea" : "Expand idea"}
                    onClick={() => setOpen(isOpen ? null : id)}
                  >
                    <Icon icon={isOpen ? ChevronDown : ChevronRight} size={16} />
                  </button>
                  <button
                    className={styles.roundBtn}
                    aria-label="Remove idea"
                    onClick={() =>
                      remove.mutate(id, { onError: (e) => pushToast(errorMessage(e, "Couldn't remove."), "error") })
                    }
                  >
                    <Icon icon={X} size={16} />
                  </button>
                </div>

                {isOpen && (
                  <div className={styles.savedBody}>
                    {idea.body && <p className={styles.savedDesc}>{idea.body}</p>}
                    <div className={styles.savedActions}>
                      <button
                        className={idea.status === "done" ? styles.verdictOn : styles.verdictBtn}
                        onClick={() => setStatus(idea, "done")}
                      >
                        Worked
                      </button>
                      <button
                        className={idea.status === "dropped" ? styles.verdictOn : styles.verdictBtn}
                        onClick={() => setStatus(idea, "dropped")}
                      >
                        Didn&apos;t
                      </button>
                    </div>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

// ---- the screen (capability gate + composition) ---------------------------------------------------------------

/**
 * The Content tab (content). Gated on `releases:read` — the content audience
 * (owner/AV, manager, label, content; NOT booking/touring), enforced server-side by the composed `/content`
 * endpoint, which via the org lens also serves the roster surface (Label/Mgmt). The AI tools are
 * user-initiated fast jobs behind the Gemini seam — provider-guarded, per-user rate-limited.
 */
export function ContentScreen() {
  const canSee = useCapability("releases:read");
  const q = useContentStats();
  // On the phone the AI tools sit BELOW the per-platform stats (Ryan, ART-184 round two: "both of these
  // sections should be below the stats section"). Desktop keeps the hero-beside-AI-rail layout.
  const isDesktop = !usePhoneLayout();

  if (!canSee) {
    return (
      <Page>
        <EmptyState
          title="Content isn't available for your role"
          description="The Content tab is available to owner/AV, management, label, and content roles."
        />
      </Page>
    );
  }

  return (
    // No page banner. The nav tab already names this screen and the hero states its own subject; an H1
    // reading "Content" over a hero reading "REACH · TOTAL FOLLOWERS" was a title for a title.
    <Page>
      <div className={styles.modules}>
        {isDesktop ? (
          <>
            {/* Row 1 is the hero BESIDE the AI rail, not above it. The two AI tools are short cards that read
                as a sidebar next to the hero's height; stacked full-width they pushed the per-platform
                breakdown — the actual subject of the page — below the fold. */}
            <div className={styles.topRow}>
              <ReachHeroModule q={q} />
              <div className={styles.aiRail}>
                <GetTheData />
                <IdeasModule />
              </div>
            </div>
            <PlatformModule q={q} />
            {/* The two tool cards are peers and each is short — full-width they were two long thin bands at
                the bottom of a tall page. With the hashtag card hidden, Saved ideas stands alone at full
                width rather than in half a row. */}
            {SHOW_HASHTAG_GENERATOR ? (
              <div className={styles.toolRow}>
                <HashtagModule />
                <SavedIdeasModule />
              </div>
            ) : (
              <SavedIdeasModule />
            )}
          </>
        ) : (
          // Mobile order (Ryan, ART-184): hero → the per-platform stats → then ALL the AI tools, stacked
          // full-width beneath them. "Get the data" and "Need ideas" belong under the stats, not beside the
          // hero, on the phone.
          <>
            <ReachHeroModule q={q} />
            <PlatformModule q={q} />
            <GetTheData />
            <IdeasModule />
            {SHOW_HASHTAG_GENERATOR && <HashtagModule />}
            <SavedIdeasModule />
          </>
        )}
      </div>
    </Page>
  );
}

/** The `/content/:platform` drill-down — one platform's full breakdown (a bookmarkable deep-link). */
export function ContentPlatformScreen({ platform, onBack }: { platform: string; onBack?: () => void }) {
  const canSee = useCapability("releases:read");
  const q = useContentStats();
  const view: ContentView | undefined = q.data;
  const found = (view?.platforms ?? []).find((p) => p.platform === platform);

  if (!canSee) {
    return (
      <Page>
        <EmptyState title="Content isn't available for your role" description="Ask an owner or manager for access." />
      </Page>
    );
  }

  return (
    <Page
      title={label(platform)}
      actions={
        onBack && (
          <button className={styles.clear} onClick={onBack}>
            ← Back to Content
          </button>
        )
      }
    >
      {q.isLoading ? (
        <Skeleton variant="tile" />
      ) : found ? (
        <PlatformPanel platform={found} />
      ) : (
        <EmptyState title={REACH_EMPTY} description="Connect this platform in Settings → Connections." />
      )}
    </Page>
  );
}
